import { Voucher } from '../types';
import { PdfTablePropsColumn, PdfTablePropsRow } from '../../../components/Pdfs/PdfComponents/PdfTable';
import { BillingContactPDFType } from '../../../components/Pdfs/PdfComponents/PdfBillingContactDetails';
import { taytanFormatNumberToString } from '../../../utils/mathUtils';
import { getPdfAgentInfo } from '../../travelAgencyAccounts/helpers';
import { PdfInfoBuilderContext, BuiltPdfInfo } from './types';
import { getCurrencyDetails, getClientCurrencies, buildRateLine } from './helpers';

/**
 * Build pdfInfo for Agent Commission Invoice
 * Table: Details | Currency | Local Currency
 * Rows: Commission, VAT, Total
 */
export function buildAgentInvoicePdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const info = voucher.info as any;
    const paymentMethod = info?.paymentMethodObject;
    const { localCurrency, primaryCurrency } = getClientCurrencies(context.client, context.currencies);
    const { code: currencyCode, currency: docCurrency } = getCurrencyDetails(
        info?.currency,
        context.currencies
    );

    // ── Secondary currency column ─────────────────────────────────────────────
    // Normally the client's LOCAL currency (e.g. ILS). But when the document is
    // itself in the local currency, a second ILS column is redundant — show the
    // PRIMARY currency (e.g. USD) instead, converted at the document-date rate.
    const isLocalDoc = !!localCurrency && !!docCurrency && docCurrency._id === localCurrency._id;
    const secondaryCurrency = isLocalDoc ? primaryCurrency : localCurrency;
    const localLabel = secondaryCurrency?.code || '';

    // Per-document toggle (info.printLocalCurrency). Legacy docs (undefined) keep
    // the column. Never render a second column that duplicates the primary one.
    const showSecondary =
        info?.printLocalCurrency !== false &&
        !!secondaryCurrency &&
        localLabel !== currencyCode;

    // Build columns
    const columns: PdfTablePropsColumn[] = [
        { title: 'Details', fieldName: 'details', style: { width: '60%' } },
        { title: currencyCode, fieldName: 'amount', style: { width: '20%' } }
    ];

    if (showSecondary) {
        columns.push({
            title: localLabel,
            fieldName: localLabel,
            style: { width: '20%' }
        });
    }

    // Commission: prefer info.commission, then info.markup (import stores commission as markup), then paymentMethodObject
    const commissionAmount = Number(info?.commission) || Number(info?.markup) || Number(paymentMethod?.firstPayment) || 0;
    const vatAmount = Number(info?.vatOnCommission) || 0;
    // ACI total = commission + VAT on commission. Prefer an explicit
    // sum over info.amount so legacy rows that stored base-or-total
    // ambiguously still render correctly.
    const totalAmount = commissionAmount + vatAmount || Number(info?.amount) || 0;

    // Rate held on the doc is the doc→local rate for a foreign doc, or the
    // local↔primary (e.g. USD→ILS) rate for a local doc (see RateControlBox).
    const fallbackRate = isLocalDoc
        ? context.getConvertRate(primaryCurrency, localCurrency)
        : context.getConvertRate(info?.currency, localCurrency);
    const rateToLocal = Number(info?.rate) || (fallbackRate as any)?.exchangeRateSell || 1;

    // For a foreign doc the second column is the LOCAL currency, and the doc
    // stores `amountLocal` = the exact ILS total (the same value reports use).
    // Derive the column from it so the PDF, reports, and the saved value never
    // drift by a rounding cent — and a stale/market rate can't leak in. Falls
    // back to rate for legacy rows (no amountLocal) or a local-currency doc.
    const storedLocal = Number(info?.amountLocal) || 0;
    const docTotal = Number(info?.amount) || totalAmount;
    const effectiveLocalRate =
        !isLocalDoc && storedLocal > 0 && docTotal > 0 ? storedLocal / docTotal : rateToLocal;

    const round2 = (n: number): number => Math.round(n * 100) / 100;
    // Foreign doc: secondary (local) = amount * effectiveLocalRate. Local doc:
    // secondary (primary/USD) = amount / rate (rate is USD→local for the date).
    const toSecondary = (n: number): number =>
        isLocalDoc ? (rateToLocal ? n / rateToLocal : n) : n * effectiveLocalRate;
    // Total ties to the exact stored local amount; VAT is the RESIDUAL so
    // Commission + VAT always sums to the total (no 1-cent drift).
    const finalTotalLocal = !isLocalDoc && storedLocal > 0 ? storedLocal : round2(toSecondary(totalAmount));
    const finalCommissionLocal = round2(toSecondary(commissionAmount));
    const finalVatLocal = vatAmount !== 0 ? round2(finalTotalLocal - finalCommissionLocal) : 0;

    // Build rows with Commission and VAT
    const rows: PdfTablePropsRow[] = [
        {
            details: 'Commission',
            amount: taytanFormatNumberToString(commissionAmount),
            [localLabel]: taytanFormatNumberToString(finalCommissionLocal)
        }
    ];

    // Show VAT row if there's VAT
    if (vatAmount !== 0) {
        rows.push({
            details: 'VAT',
            amount: taytanFormatNumberToString(vatAmount),
            [localLabel]: taytanFormatNumberToString(finalVatLocal)
        });
    }

    const summaryLine: PdfTablePropsRow = {
        details: 'Total',
        amount: taytanFormatNumberToString(totalAmount),
        [localLabel]: taytanFormatNumberToString(finalTotalLocal)
    };

    // Get agent info from context or stored fields
    // For ACI, targetType is 'agent' (the travel agency receiving commission)
    let agentInfo: BillingContactPDFType | null = null;
    if (voucher.targetType === 'agent' && context.travelAgencyAccount) {
        agentInfo = getPdfAgentInfo(context.travelAgencyAccount);
    }
    // Fallback: build from stored info fields
    if (!agentInfo && info?.travelAgentName) {
        agentInfo = {
            name: info.travelAgentName,
            address: info?.agentAddress || '',
            city: info?.agentCity || '',
            country: info?.agentCountry || '',
            postalCode: '',
            vatNumber: info?.agentVatNumber || '',
            phones: []
        };
    }

    // Get linked invoice number from coveredDocuments (resolved), paymentMethodObject.reference, or coveredBy
    const coveredDoc = context.coveredDocuments?.[0];
    const linkedInvoiceNumber =
        coveredDoc?.number ||
        paymentMethod?.reference ||
        info?.coveredBy?.[0]?.number ||
        '';

    // ACI Reference number (agent's own reference)
    const aciRef = info?.aciRef || '';

    // Rate line for currency conversion display
    const rateLine = buildRateLine(currencyCode, rateToLocal, localCurrency?.code || '');

    return {
        ...baseInfo,
        columns,
        rows,
        summaryLine,
        agentInfo,
        topLeftDetailsText: undefined,
        bottomDetails: info?.for || '',
        linkedInvoiceNumber,
        aciRef,
        rateLine,
        rateTableColumns: [],
        rateTableRows: []
    };
}
